import { useEffect, useState } from "react";
import { Brand } from "./Brand";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Aperture, Menu, Coins, LogOut, ShieldCheck, LayoutDashboard } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";

const links = [
  { to: "/", label: "Início", id: "inicio" },
  { to: "/galeria", label: "Galeria", id: "galeria" },
  { to: "/concursos", label: "Concursos", id: "concursos" },
];

const linkCls = ({ isActive }) =>
  `font-mono-tech text-xs uppercase tracking-[0.18em] transition-colors duration-200 ${isActive ? "text-cyan-300" : "text-slate-400 hover:text-white"}`;

const UserLinks = ({ user, onLogout, mobile }) => (
  <div className={`flex ${mobile ? "flex-col items-start gap-5" : "items-center gap-4"}`}>
    <span data-testid="nav-credits-badge" className="flex items-center gap-1.5 rounded-full border border-lime-400/30 bg-lime-400/10 px-3 py-1 font-mono-tech text-xs text-lime-300">
      <Coins className="h-3.5 w-3.5" /> {user.photo_credits} crédito(s)
    </span>
    <NavLink to="/painel" data-testid="nav-link-painel" className={linkCls}>
      <span className="flex items-center gap-1.5"><LayoutDashboard className="h-4 w-4" /> Meu painel</span>
    </NavLink>
    {user.role === "admin" && (
      <NavLink to="/proprietario" data-testid="nav-link-owner-admin" className={linkCls}>
        <span className="flex items-center gap-1.5 text-lime-300"><ShieldCheck className="h-4 w-4" /> Proprietário</span>
      </NavLink>
    )}
    <button data-testid="nav-logout-btn" onClick={onLogout} className="text-slate-400 transition-colors hover:text-red-400" aria-label="Sair">
      <LogOut className="h-4 w-4" />
    </button>
  </div>
);

const GuestLinks = ({ mobile }) => (
  <div className={`flex ${mobile ? "flex-col items-start gap-4" : "items-center gap-3"}`}>
    <Link to="/entrar" data-testid="nav-login-link" className="font-mono-tech text-xs uppercase tracking-[0.18em] text-slate-300 hover:text-white">Entrar</Link>
    <Link to="/cadastro" data-testid="nav-register-btn" className="btn-neon rounded-full px-5 py-2 text-sm font-semibold">
      Cadastre-se grátis
    </Link>
  </div>
);

export const Navbar = () => {
  const { user, settings, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (settings?.site_name) document.title = `${settings.site_name} | Concursos de Fotografia Esportiva`;
  }, [settings?.site_name]);
  const onLogout = async () => {
    await logout();
    setOpen(false);
    nav("/");
  };
  return (
    <header className="sticky top-0 z-50">
      {settings?.announcement && (
        <div data-testid="announcement-bar" className="bg-lime-400 px-4 py-1.5 text-center font-mono-tech text-[11px] uppercase tracking-[0.15em] text-[#0A0A0F]">
          {settings.announcement}
        </div>
      )}
      <div className="glass border-x-0 border-t-0">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 lg:px-8">
          <Link to="/" data-testid="nav-logo" className="group flex items-center gap-2">
            <Aperture className="h-7 w-7 text-lime-400 transition-transform duration-500 group-hover:rotate-180" />
            <Brand name={settings?.site_name || "D+Photo"} className="text-2xl" />
          </Link>
          <nav className="hidden items-center gap-8 md:flex">
            {links.map((l) => (
              <NavLink key={l.to} to={l.to} end data-testid={`nav-link-${l.id}`} className={linkCls}>{l.label}</NavLink>
            ))}
          </nav>
          <div className="hidden md:block">{user ? <UserLinks user={user} onLogout={onLogout} /> : <GuestLinks />}</div>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button className="md:hidden" data-testid="nav-mobile-menu-btn" aria-label="Menu"><Menu className="h-6 w-6" /></button>
            </SheetTrigger>
            <SheetContent className="border-white/10 bg-[#0A0A0F]" onClick={(e) => e.target.closest("a") && setOpen(false)}>
              <div className="mt-10 flex flex-col gap-6">
                {links.map((l) => (
                  <NavLink key={l.to} to={l.to} end data-testid={`mobile-nav-link-${l.id}`} className={linkCls}>{l.label}</NavLink>
                ))}
                <div className="h-px bg-white/10" />
                {user ? <UserLinks user={user} onLogout={onLogout} mobile /> : <GuestLinks mobile />}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};
